/**
 * Product Zoom Modal - Desktop and Mobile
 * Handles image zoom, navigation, and variant selection
 */

class ProductZoomModal {
  constructor() {
    this.modal = document.getElementById('ProductZoomModal');
    this.zoomImage = document.getElementById('ZoomMainImage');
    this.zoomVideo = document.getElementById('ZoomMainVideo');
    this.closeBtn = this.modal?.querySelector('.product-zoom-close');
    this.overlay = this.modal?.querySelector('.product-zoom-overlay');
    this.zoomSlider = this.modal?.querySelector('.zoom-slider');
    this.zoomInBtn = this.modal?.querySelector('.zoom-in');
    this.zoomOutBtn = this.modal?.querySelector('.zoom-out');
    this.zoomPercentage = this.modal?.querySelector('.zoom-percentage');
    this.thumbnails = this.modal?.querySelectorAll('.zoom-thumbnail');
    this.productImages = document.querySelectorAll('.product__media-img');
    
    // Mobile navigation arrows
    this.mobileNavLeft = this.modal?.querySelector('.mobile-nav-left');
    this.mobileNavRight = this.modal?.querySelector('.mobile-nav-right');
    this.thumbnailNavUp = this.modal?.querySelector('.thumbnail-nav-up');
    this.thumbnailNavDown = this.modal?.querySelector('.thumbnail-nav-down');
    this.colorOptions = this.modal?.querySelectorAll('.zoom-color-option');
    this.sizeOptions = this.modal?.querySelectorAll('.zoom-size-option');

    this.currentZoom = 1;
    this.isDragging = false;
    this.startX = 0;
    this.startY = 0;
    this.initialX = 0;
    this.initialY = 0;
    this.currentX = 0;
    this.currentY = 0;
    this.currentImageIndex = 0;
    this.currentVariantId = null;
    this.currentSelectedSize = null;
    this.currentSelectedColor = null;

    this.productVariants = this.getProductVariants();
    this.sizeOptionIndex = window.sizeOptionIndex || -1;
    this.colorOptionIndex = window.colorOptionIndex || -1;

    this.init();
  }

  getProductVariants() {
    // This will be populated by Liquid template
    return window.productVariants || [];
  }

  init() {
    if (!this.modal) return;

    this.setupEventListeners();
    this.initializeVariantSelection();
    this.preloadImages();
  }

  setupEventListeners() {
    // Image click handlers
    this.productImages.forEach((img, index) => {
      img.addEventListener('click', (e) => {
        e.preventDefault();
        this.openZoomModal(img.src, img.alt, index);
      });
      
      img.addEventListener('touchend', (e) => {
        e.preventDefault();
        this.openZoomModal(img.src, img.alt, index);
      });
    });

    // Mobile zoom button
    const mobileZoomBtn = document.getElementById('MobileZoomBtn');
    if (mobileZoomBtn) {
      mobileZoomBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const mainImage = document.getElementById('MainProductImage');
        if (mainImage) {
          this.openZoomModal(mainImage.src, mainImage.alt, this.currentMediaIndex());
        }
      });

      mobileZoomBtn.addEventListener('touchend', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const mainImage = document.getElementById('MainProductImage');
        if (mainImage) {
          this.openZoomModal(mainImage.src, mainImage.alt, this.currentMediaIndex());
        }
      });
    }

    // Close modal
    this.closeBtn?.addEventListener('click', () => this.closeZoomModal());
    this.overlay?.addEventListener('click', () => this.closeZoomModal());

    // Keyboard events
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.style.display === 'flex') {
        this.closeZoomModal();
      }
    });

    // Zoom controls
    this.zoomSlider?.addEventListener('input', (e) => {
      this.currentZoom = parseFloat(e.target.value);
      if (this.currentZoom <= 1) {
        this.currentZoom = 1;
        this.currentX = 0;
        this.currentY = 0;
      }
      this.updateImageTransform();
      this.updateZoomPercentage();
      this.updateCursor();
    });

    this.zoomInBtn?.addEventListener('click', () => {
      this.currentZoom = Math.min(3, this.currentZoom + 0.2);
      this.zoomSlider.value = this.currentZoom;
      this.updateImageTransform();
      this.updateZoomPercentage();
      this.updateCursor();
    });

    this.zoomOutBtn?.addEventListener('click', () => {
      this.currentZoom = Math.max(1, this.currentZoom - 0.2);
      if (this.currentZoom <= 1) {
        this.currentZoom = 1;
        this.currentX = 0;
        this.currentY = 0;
      }
      this.zoomSlider.value = this.currentZoom;
      this.updateImageTransform();
      this.updateZoomPercentage();
      this.updateCursor();
    });

    // Thumbnails
    this.thumbnails?.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', () => {
        const productImg = this.productImages[index];
        if (productImg) {
          // Résolution adaptative : 1200px mobile, 2400px desktop
          const targetWidth = this.isDesktop() ? 2400 : 1200;
          const highResSrc = productImg.src.replace(/width=\d+/, `width=${targetWidth}`);
          this.zoomImage.src = highResSrc;
          this.zoomImage.alt = productImg.alt;
          
          this.currentZoom = 1;
          this.currentX = 0;
          this.currentY = 0;
          this.updateImageTransform();
          this.updateZoomPercentage();
          this.zoomSlider.value = 1;
          
          this.currentImageIndex = index;
          
          this.thumbnails.forEach(thumb => thumb.classList.remove('active'));
          thumbnail.classList.add('active');
          
          this.updateThumbnailNavigation();
        }
      });
    });

    // Thumbnail navigation
    this.thumbnailNavUp?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.navigateToImage('up');
    });

    this.thumbnailNavDown?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.navigateToImage('down');
    });

    // Mobile navigation arrows
    this.mobileNavLeft?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.navigateToImage('left');
    });

    this.mobileNavRight?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.navigateToImage('right');
    });

    // Swipe horizontal dans la lightbox : pose sur le conteneur, donc actif
    // sur l'image comme sur la video. Ignore des qu'on est en train de zoomer.
    const zoomSurface = this.modal?.querySelector('.product-zoom-image-container');
    if (zoomSurface) {
      let swipeX = 0;
      let swipeY = 0;
      let tracking = false;

      zoomSurface.addEventListener('touchstart', (e) => {
        tracking = e.touches.length === 1 && this.currentZoom <= 1.25;
        if (tracking) {
          swipeX = e.touches[0].clientX;
          swipeY = e.touches[0].clientY;
        }
      }, { passive: true });

      zoomSurface.addEventListener('touchend', (e) => {
        if (!tracking || !e.changedTouches || !e.changedTouches.length) return;
        tracking = false;
        const dx = swipeX - e.changedTouches[0].clientX;
        const dy = Math.abs(swipeY - e.changedTouches[0].clientY);
        if (Math.abs(dx) > 50 && dy < 100) {
          this.navigateToImage(dx > 0 ? 'right' : 'left');
        }
      }, { passive: true });
    }


    // Size options
    this.sizeOptions?.forEach(sizeOption => {
      sizeOption.addEventListener('click', (e) => {
        e.stopPropagation();
        
        const isDisabled = sizeOption.classList.contains('disabled');
        const isAvailable = sizeOption.dataset.available === 'true';
        
        if (isDisabled || !isAvailable) {
          e.preventDefault();
          return;
        }
        
        const selectedSize = sizeOption.dataset.size;
        if (selectedSize) {
          this.currentSelectedSize = selectedSize;
          this.findMatchingVariant();
          this.updatePickersState();
          this.updateMainProductForm();
        }
      });
    });

    // Color options
    this.colorOptions?.forEach(colorOption => {
      colorOption.addEventListener('click', (e) => {
        e.stopPropagation();
        
        if (colorOption.classList.contains('disabled') || colorOption.dataset.available === 'false') {
          e.preventDefault();
          return;
        }
        
        const selectedColor = colorOption.dataset.color;
        if (selectedColor) {
          this.currentSelectedColor = selectedColor;
          this.findMatchingVariant();
          this.updatePickersState();
          this.updateMainProductForm();
          this.afficherPhotoDeLaCouleur(colorOption);
        }
      });
    });

    // Prevent modal close when clicking on image or controls
    this.modal.querySelector('.product-zoom-container')?.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    // Desktop-specific drag handling
    if (this.isDesktop()) {
      this.setupDesktopDrag();
    }

    // Mobile-specific touch handling
    if (!this.isDesktop()) {
      this.setupMobileTouch();
    }
  }

  setupDesktopDrag() {
    let isMouseDragging = false;
    let mouseStartX = 0;
    let mouseStartY = 0;
    let startTransformX = 0;
    let startTransformY = 0;

    this.zoomImage.addEventListener('mousedown', (e) => {
      if (this.currentZoom > 1) {
        e.preventDefault();
        e.stopPropagation();
        isMouseDragging = true;
        mouseStartX = e.clientX;
        mouseStartY = e.clientY;
        startTransformX = this.currentX;
        startTransformY = this.currentY;
        this.zoomImage.style.cursor = 'grabbing';
        document.body.style.userSelect = 'none';
      }
    });

    document.addEventListener('mousemove', (e) => {
      if (isMouseDragging && this.currentZoom > 1) {
        e.preventDefault();
        e.stopPropagation();
        const deltaX = e.clientX - mouseStartX;
        const deltaY = e.clientY - mouseStartY;
        this.currentX = startTransformX + deltaX;
        this.currentY = startTransformY + deltaY;
        
        requestAnimationFrame(() => {
          this.updateImageTransform();
        });
      }
    });

    document.addEventListener('mouseup', (e) => {
      if (isMouseDragging) {
        e.preventDefault();
        e.stopPropagation();
        isMouseDragging = false;
        document.body.style.userSelect = '';
        this.updateImageTransform();
      }
    });

    // Mouse wheel zoom
    const imageContainer = this.modal.querySelector('.product-zoom-image-container');
    imageContainer?.addEventListener('wheel', (e) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      this.currentZoom = Math.max(1, Math.min(3, this.currentZoom + delta));
      this.zoomSlider.value = this.currentZoom;
      this.updateImageTransform();
      this.updateZoomPercentage();
    });
  }

  setupMobileTouch() {
    // État unifié (pas de variables locales séparées)
    let isPinching = false;
    let isDragging = false;
    let startDistance = 0;
    let startScale = 1;
    let startTranslateX = 0;
    let startTranslateY = 0;
    let startTouchX = 0;
    let startTouchY = 0;
    let animationFrame = null;

    // Fonction pour calculer les limites de drag
    const getDragLimits = (scale) => {
      const img = this.zoomImage;
      const container = img.parentElement;
      
      const imgWidth = img.naturalWidth || img.width;
      const imgHeight = img.naturalHeight || img.height;
      
      const scaledWidth = imgWidth * scale;
      const scaledHeight = imgHeight * scale;
      
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;
      
      // Limites maximales de déplacement
      const maxX = Math.max(0, (scaledWidth - containerWidth) / 2);
      const maxY = Math.max(0, (scaledHeight - containerHeight) / 2);
      
      return { maxX, maxY };
    };

    // Fonction pour limiter le drag (avec rubber band effect)
    const constrainDrag = (x, y, scale) => {
      const limits = getDragLimits(scale);
      
      // Rubber band effect : permet de dépasser légèrement
      const rubberBand = (value, max) => {
        if (Math.abs(value) <= max) return value;
        const excess = Math.abs(value) - max;
        const sign = value > 0 ? 1 : -1;
        // Formule rubber band : résistance exponentielle
        return sign * (max + excess * 0.3);
      };
      
      return {
        x: rubberBand(x, limits.maxX),
        y: rubberBand(y, limits.maxY)
      };
    };

    // Update avec RAF pour performance
    const updateImageTransform = () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
      
      animationFrame = requestAnimationFrame(() => {
        // Utiliser la méthode globale pour cohérence
        this.updateImageTransform();
        
        // Mise à jour du curseur
        this.zoomImage.style.cursor = 
          this.currentZoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default';
      });
    };

    const getDistance = (touches) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    // Touch start
    this.zoomImage.addEventListener('touchstart', (e) => {
      // ✅ Ne bloquer que si nécessaire
      if (this.currentZoom > 1 || e.touches.length === 2) {
        e.preventDefault();
      }
      
      if (e.touches.length === 2) {
        // Pinch start
        isPinching = true;
        isDragging = false;
        startDistance = getDistance(e.touches);
        startScale = this.currentZoom;
      } else if (e.touches.length === 1) {
        // Drag start (seulement si zoomé)
        if (this.currentZoom > 1) {
          e.preventDefault();
          isDragging = true;
          isPinching = false;
          startTranslateX = this.currentX;
          startTranslateY = this.currentY;
          startTouchX = e.touches[0].clientX;
          startTouchY = e.touches[0].clientY;
        }
      }
    }, { passive: false });

    // Touch move
    this.zoomImage.addEventListener('touchmove', (e) => {
      if (isPinching && e.touches.length === 2) {
        e.preventDefault();
        
        // Pinch zoom
        const currentDistance = getDistance(e.touches);
        const scaleChange = currentDistance / startDistance;
        
        // ✅ Smooth scaling avec easing
        const targetScale = startScale * scaleChange;
        this.currentZoom = Math.max(1, Math.min(3, targetScale));
        
        // ✅ Recentrer légèrement pendant le zoom
        if (this.currentZoom <= 1.2) {
          this.currentX *= 0.9;
          this.currentY *= 0.9;
        }
        
        updateImageTransform();
        
      } else if (isDragging && e.touches.length === 1 && this.currentZoom > 1) {
        e.preventDefault();
        
        // Drag
        const deltaX = e.touches[0].clientX - startTouchX;
        const deltaY = e.touches[0].clientY - startTouchY;
        
        const newX = startTranslateX + deltaX;
        const newY = startTranslateY + deltaY;
        
        // ✅ Appliquer les limites avec rubber band
        const constrained = constrainDrag(newX, newY, this.currentZoom);
        this.currentX = constrained.x;
        this.currentY = constrained.y;
        
        updateImageTransform();
      }
    }, { passive: false });

    // Touch end
    this.zoomImage.addEventListener('touchend', (e) => {
      if (e.touches.length === 0) {
        isPinching = false;
        
        // ✅ Reset avec transition smooth si zoom < threshold
        if (this.currentZoom < 1.15) {
          // Transition CSS temporaire
          this.zoomImage.style.transition = 'transform 0.3s ease-out';
          
          this.currentZoom = 1.2;
          this.currentX = 0;
          this.currentY = 0;
          updateImageTransform();
          
          // Retirer la transition après
          setTimeout(() => {
            this.zoomImage.style.transition = '';
          }, 300);
        } else {
          // ✅ Snap back si hors limites
          const limits = getDragLimits(this.currentZoom);
          
          let needsSnap = false;
          if (Math.abs(this.currentX) > limits.maxX) {
            this.currentX = Math.sign(this.currentX) * limits.maxX;
            needsSnap = true;
          }
          if (Math.abs(this.currentY) > limits.maxY) {
            this.currentY = Math.sign(this.currentY) * limits.maxY;
            needsSnap = true;
          }
          
          if (needsSnap) {
            this.zoomImage.style.transition = 'transform 0.2s ease-out';
            updateImageTransform();
            setTimeout(() => {
              this.zoomImage.style.transition = '';
            }, 200);
          }
        }
        
        isDragging = false;
      } else if (e.touches.length === 1) {
        // Passer de pinch à drag
        isPinching = false;
        if (this.currentZoom > 1) {
          isDragging = true;
          startTranslateX = this.currentX;
          startTranslateY = this.currentY;
          startTouchX = e.touches[0].clientX;
          startTouchY = e.touches[0].clientY;
        }
      }
    }, { passive: true });
  }


  // Index du media actuellement affiche dans la galerie mobile
  currentMediaIndex() {
    const dots = document.querySelectorAll('.product__media-dot');
    const i = Array.from(dots).findIndex((d) => d.classList.contains('active'));
    return i === -1 ? 0 : i;
  }

  // Bascule la lightbox entre <img> et <video> selon le type du media.
  // Renvoie true si c'est une video.
  showZoomMedia(index) {
    const media = (window.mediaData || [])[index];
    const isVideo = !!(media && media.type === 'video' && media.videoSrc);

    if (this.zoomVideo) {
      if (isVideo) {
        if (this.zoomVideo.getAttribute('src') !== media.videoSrc) {
          this.zoomVideo.setAttribute('src', media.videoSrc);
        }
        this.zoomVideo.poster = media.poster || media.src;
        this.zoomVideo.hidden = false;
        this.zoomVideo.play().catch(() => {});
      } else {
        this.zoomVideo.pause();
        this.zoomVideo.hidden = true;
      }
    }
    if (this.zoomImage) this.zoomImage.hidden = isVideo;
    if (this.modal) this.modal.classList.toggle('has-video', isVideo);
    return isVideo;
  }

  openZoomModal(imageSrc, imageAlt, imageIndex) {
    // Video : la lightbox bascule sur <video>, la suite ne touche que le <img> masque
    this.showZoomMedia(imageIndex);

    // Résolution adaptative : 1200px mobile, 2400px desktop
    const targetWidth = this.isDesktop() ? 2400 : 1200;
    const highResSrc = imageSrc.replace(/width=\d+/, `width=${targetWidth}`);
    this.zoomImage.src = highResSrc;
    this.zoomImage.alt = imageAlt;
    
    this.currentImageIndex = imageIndex;
    
    // Store the element that triggered the modal for focus return
    this.previousActiveElement = document.activeElement;
    
    // Store current scroll position to prevent jump on close
    this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    
    if (!this.isDesktop()) {
      this.currentZoom = 1.2;
      this.currentX = 0;
      this.currentY = 0;
      this.updateImageTransform();
      
      setTimeout(() => {
        this.initializeVariantPickers();
      }, 100);
    } else {
      this.currentZoom = 1;
      this.currentX = 0;
      this.currentY = 0;
      this.updateImageTransform();
      this.updateZoomPercentage();
      this.zoomSlider.value = 1;
      
      this.thumbnails.forEach((thumb, i) => {
        thumb.classList.toggle('active', i === imageIndex);
      });
      
      this.updateThumbnailNavigation();
      
      setTimeout(() => {
        this.initializeVariantPickers();
      }, 100);
    }
    
    this.modal.style.display = 'flex';
    this.modal.classList.remove('closing');
    this.modal.classList.add('opening');
    
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    
    // Focus trap: Set focus to close button
    setTimeout(() => {
      this.closeBtn?.focus();
    }, 100);
    document.body.style.overflow = 'hidden';
  }

  closeZoomModal() {
    if (this.zoomVideo) this.zoomVideo.pause();

    this.modal.classList.remove('opening');
    this.modal.classList.add('closing');
    
    setTimeout(() => {
      this.modal.style.display = 'none';
      this.modal.classList.remove('closing');
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      
      // Focus trap: Return focus to the element that opened the modal
      if (this.previousActiveElement) {
        this.previousActiveElement.focus();
      }
      
      // Restore scroll position to prevent jump (after focus to override any scroll)
      if (this.scrollPosition !== undefined) {
        window.scrollTo(0, this.scrollPosition);
      }
    }, 300);
  }

  updateImageTransform() {
    if (this.isDesktop()) {
      this.zoomImage.style.transform = `translate3d(${this.currentX}px, ${this.currentY}px, 0) scale(${this.currentZoom})`;
    } else {
      this.zoomImage.style.transform = `scale(${this.currentZoom}) translate(${this.currentX}px, ${this.currentY}px)`;
    }

    // Zoomee, l image peut deborder de sa boite : sinon les marges de la zone
    // d image la coupent et laissent deux bandes vides, en haut et en bas.
    this.modal?.classList.toggle('is-zoomed', this.currentZoom > 1);
  }

  updateZoomPercentage() {
    const percentage = Math.round(this.currentZoom * 100);
    this.zoomPercentage.textContent = percentage + '%';
  }

  updateCursor() {
    if (this.zoomImage) {
      this.zoomImage.style.cursor = this.currentZoom > 1 ? 'grab' : 'default';
    }
  }

  updateThumbnailNavigation() {
    this.thumbnailNavUp.disabled = false;
    this.thumbnailNavDown.disabled = false;
    this.updateThumbnailDisplay();
  }

  resetZoomPosition() {
    this.currentZoom = this.isDesktop() ? 1 : 1.2;
    this.currentX = 0;
    this.currentY = 0;

    if (this.zoomImage) this.zoomImage.style.transition = '';
    this.updateImageTransform();

    if (this.isDesktop()) {
      this.updateZoomPercentage();
      if (this.zoomSlider) this.zoomSlider.value = 1;
      this.updateCursor();
    }
  }

  navigateToImage(direction) {
    // Source de verite : mediaData, qui connait les videos. Les vignettes
    // .zoom-thumbnail n'existent qu'en desktop, d'ou le repli.
    const media = window.mediaData || [];
    const total = media.length || this.thumbnails.length;
    if (!total) return;

    let newIndex;
    if (direction === 'up' || direction === 'left') {
      newIndex = this.currentImageIndex > 0 ? this.currentImageIndex - 1 : total - 1;
    } else if (direction === 'down' || direction === 'right') {
      newIndex = this.currentImageIndex < total - 1 ? this.currentImageIndex + 1 : 0;
    } else {
      return;
    }

    this.currentImageIndex = newIndex;

    // On repart d une image cadree, jamais decalee par un deplacement precedent
    this.resetZoomPosition();

    if (media[newIndex]) {
      const isVideo = this.showZoomMedia(newIndex);
      if (!isVideo && this.zoomImage) {
        const targetWidth = this.isDesktop() ? 2400 : 1200;
        this.zoomImage.srcset = '';
        this.zoomImage.src = String(media[newIndex].src).replace(/width=\d+/, 'width=' + targetWidth);
        this.zoomImage.alt = media[newIndex].alt || '';
      }
      // La galerie de la page suit la lightbox
      document.querySelectorAll('.product__media-dot').forEach((d, i) => {
        d.classList.toggle('active', i === newIndex);
      });
    } else {
      const targetThumbnail = this.thumbnails[newIndex];
      if (targetThumbnail) targetThumbnail.click();
    }

    this.updateThumbnailDisplay();
  }

  updateThumbnailDisplay() {
    const maxVisibleThumbnails = 6;
    const totalThumbnails = this.thumbnails.length;
    
    if (totalThumbnails <= maxVisibleThumbnails) {
      this.thumbnails.forEach((thumb) => {
        thumb.style.display = 'block';
      });
      return;
    }
    
    let startIndex = Math.max(0, this.currentImageIndex - Math.floor(maxVisibleThumbnails / 2));
    let endIndex = Math.min(totalThumbnails - 1, startIndex + maxVisibleThumbnails - 1);
    
    if (endIndex - startIndex < maxVisibleThumbnails - 1) {
      startIndex = Math.max(0, endIndex - maxVisibleThumbnails + 1);
    }
    
    this.thumbnails.forEach((thumb, index) => {
      if (index >= startIndex && index <= endIndex) {
        thumb.style.display = 'block';
      } else {
        thumb.style.display = 'none';
      }
    });
  }

  initializeVariantPickers() {
    const currentVariant = this.getCurrentProductVariant();
    const urlVariantId = this.getVariantFromURL();
    let targetVariant = currentVariant;
    
    if (urlVariantId) {
      const urlVariant = this.productVariants.find(v => v.id === urlVariantId);
      if (urlVariant) {
        targetVariant = urlVariant;
      }
    }
    
    if (targetVariant) {
      if (this.sizeOptionIndex >= 0 && targetVariant.options[this.sizeOptionIndex]) {
        this.currentSelectedSize = this.handleize(targetVariant.options[this.sizeOptionIndex]);
      }
      
      if (this.colorOptionIndex >= 0 && targetVariant.options[this.colorOptionIndex]) {
        this.currentSelectedColor = this.handleize(targetVariant.options[this.colorOptionIndex]);
      }
    } else {
      if (this.sizeOptions.length > 0) {
        const firstAvailableSize = Array.from(this.sizeOptions).find(option => !option.classList.contains('disabled'));
        if (firstAvailableSize) {
          this.currentSelectedSize = firstAvailableSize.dataset.size;
        }
      }
      
      if (this.colorOptions.length > 0) {
        const firstAvailableColor = Array.from(this.colorOptions).find(option => !option.classList.contains('disabled'));
        if (firstAvailableColor) {
          this.currentSelectedColor = firstAvailableColor.dataset.color;
        }
      }
      
      if (!this.currentSelectedSize && !this.currentSelectedColor && this.productVariants.length > 0) {
        const firstVariant = this.productVariants[0];
        if (this.sizeOptionIndex >= 0 && firstVariant.options[this.sizeOptionIndex]) {
          this.currentSelectedSize = this.handleize(firstVariant.options[this.sizeOptionIndex]);
        }
        if (this.colorOptionIndex >= 0 && firstVariant.options[this.colorOptionIndex]) {
          this.currentSelectedColor = this.handleize(firstVariant.options[this.colorOptionIndex]);
        }
      }
    }
    
    this.findMatchingVariant();
    this.updatePickersState();
  }

  getCurrentProductVariant() {
    const variantSelects = document.querySelector('variant-selects');
    if (variantSelects) {
      const selectedVariantScript = variantSelects.querySelector('script[data-selected-variant]');
      if (selectedVariantScript) {
        try {
          return JSON.parse(selectedVariantScript.textContent);
        } catch (e) {
          console.error('Error parsing selected variant:', e);
        }
      }
    }
    
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const selectedOptions = [];
      const optionInputs = productForm.querySelectorAll('input[name*="[option"]:checked, select[name*="[option"]');
      
      for (const input of optionInputs) {
        selectedOptions.push(input.value);
      }
      
      if (selectedOptions.length > 0) {
        for (const variant of this.productVariants) {
          if (variant.options.length === selectedOptions.length) {
            let matches = true;
            for (let i = 0; i < selectedOptions.length; i++) {
              if (this.handleize(variant.options[i]) !== this.handleize(selectedOptions[i])) {
                matches = false;
                break;
              }
            }
            if (matches) return variant;
          }
        }
      }
    }
    
    if (this.productVariants.length > 0) {
      return this.productVariants.find(v => v.available) || this.productVariants[0];
    }
    
    return null;
  }

  findMatchingVariant() {
    let matchingVariant = null;
    
    for (const variant of this.productVariants) {
      if (!variant.available) continue;
      
      let sizeMatch = true;
      let colorMatch = true;
      
      if (this.sizeOptionIndex >= 0 && this.currentSelectedSize) {
        sizeMatch = this.handleize(variant.options[this.sizeOptionIndex]) === this.currentSelectedSize;
      }
      
      if (this.colorOptionIndex >= 0 && this.currentSelectedColor) {
        colorMatch = this.handleize(variant.options[this.colorOptionIndex]) === this.currentSelectedColor;
      }
      
      if (sizeMatch && colorMatch) {
        matchingVariant = variant.id;
        break;
      }
    }
    
    if (!matchingVariant) {
      const firstAvailable = this.productVariants.find(v => v.available);
      if (firstAvailable) {
        matchingVariant = firstAvailable.id;
      }
    }
    
    this.currentVariantId = matchingVariant;
  }

  // Choisir une couleur dans la lightbox affiche sa photo. Les medias sont
  // repartis par couleur avec la convention des alt du theme : "p1 -" pour la
  // 1re couleur, "p2 -" pour la 2e, comme sur la fiche produit. Sans photo
  // reperee, on ne bouge pas plutot que d afficher la mauvaise.
  afficherPhotoDeLaCouleur(colorOption) {
    const position = parseInt(colorOption.dataset.colorPosition, 10);
    if (!position) return;

    const media = window.mediaData || [];
    const index = media.findIndex((m) => {
      const trouve = /^\s*p(\d+)(?!\d)/i.exec(m && m.alt ? m.alt : '');
      return trouve && Number(trouve[1]) === position;
    });

    if (index === -1 || index === this.currentImageIndex) return;
    this.afficherImage(index);
  }

  // Affiche un media precis, sans passer par les fleches
  afficherImage(index) {
    const media = window.mediaData || [];
    if (!media[index]) return;

    this.currentImageIndex = index;
    this.resetZoomPosition();

    const isVideo = this.showZoomMedia(index);
    if (!isVideo && this.zoomImage) {
      const targetWidth = this.isDesktop() ? 2400 : 1200;
      this.zoomImage.srcset = '';
      this.zoomImage.src = String(media[index].src).replace(/width=\d+/, 'width=' + targetWidth);
      this.zoomImage.alt = media[index].alt || '';
    }

    this.thumbnails.forEach((vignette, i) => vignette.classList.toggle('active', i === index));
    document.querySelectorAll('.product__media-dot').forEach((point, i) => {
      point.classList.toggle('active', i === index);
    });

    this.updateThumbnailDisplay();
  }

  updatePickersState() {
    this.updateDynamicAvailability();
    
    this.sizeOptions.forEach(option => {
      const isActive = option.dataset.size === this.currentSelectedSize;
      option.classList.toggle('active', isActive);
    });

    this.colorOptions.forEach(option => {
      const isActive = option.dataset.color === this.currentSelectedColor;
      option.classList.toggle('active', isActive);
    });
  }

  updateDynamicAvailability() {
    if (this.currentSelectedColor && this.sizeOptions.length > 0) {
      this.sizeOptions.forEach(sizeOption => {
        const sizeValue = sizeOption.dataset.size;
        const isAvailable = this.isOptionCombinationAvailable(sizeValue, this.currentSelectedColor);
        
        sizeOption.classList.toggle('disabled', !isAvailable);
        sizeOption.dataset.available = isAvailable.toString();
      });
    }
    
    if (this.currentSelectedSize && this.colorOptions.length > 0) {
      this.colorOptions.forEach(colorOption => {
        const colorValue = colorOption.dataset.color;
        const isAvailable = this.isOptionCombinationAvailable(this.currentSelectedSize, colorValue);
        
        colorOption.classList.toggle('disabled', !isAvailable);
        colorOption.dataset.available = isAvailable.toString();
      });
    }
  }

  isOptionCombinationAvailable(sizeValue, colorValue) {
    for (const variant of this.productVariants) {
      if (!variant.available) continue;
      
      let sizeMatch = true;
      let colorMatch = true;
      
      if (this.sizeOptionIndex >= 0 && sizeValue) {
        sizeMatch = this.handleize(variant.options[this.sizeOptionIndex]) === sizeValue;
      }
      
      if (this.colorOptionIndex >= 0 && colorValue) {
        colorMatch = this.handleize(variant.options[this.colorOptionIndex]) === colorValue;
      }
      
      if (sizeMatch && colorMatch) {
        return true;
      }
    }
    
    return false;
  }

  updateMainProductForm() {
    const mainProductForm = document.querySelector('product-form form');
    if (mainProductForm && this.currentVariantId) {
      const variantInput = mainProductForm.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = this.currentVariantId;
        const changeEvent = new Event('change', { bubbles: true });
        variantInput.dispatchEvent(changeEvent);
      }
    }
    
    this.updateMainProductVisualSelectors();
  }

  updateMainProductVisualSelectors() {
    if (this.currentSelectedSize) {
      const mainSizeInputs = document.querySelectorAll('.product__info-container input[type="radio"]');
      
      mainSizeInputs.forEach(input => {
        const label = input.nextElementSibling;
        
        if (label && (
          label.textContent.trim().toLowerCase() === this.currentSelectedSize.toLowerCase() ||
          label.textContent.trim().toLowerCase() === this.currentSelectedSize.replace('-', ' ').toLowerCase() ||
          input.value.toLowerCase() === this.currentSelectedSize.toLowerCase() ||
          input.value.toLowerCase().includes(this.currentSelectedSize.replace('-', ' ').toLowerCase())
        )) {
          input.checked = true;
          const changeEvent = new Event('change', { bubbles: true });
          input.dispatchEvent(changeEvent);
        }
      });
    }

    if (this.currentSelectedColor) {
      const mainColorInputs = document.querySelectorAll('.product__info-container input[type="radio"]');
      mainColorInputs.forEach(input => {
        const label = input.nextElementSibling;
        if (label && (
          label.textContent.trim().toLowerCase() === this.currentSelectedColor.replace('-', ' ').toLowerCase() ||
          input.value.toLowerCase().includes(this.currentSelectedColor.toLowerCase())
        )) {
          input.checked = true;
          const changeEvent = new Event('change', { bubbles: true });
          input.dispatchEvent(changeEvent);
        }
      });
    }
  }

  getVariantFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    return variantId ? variantId : null;
  }

  handleize(str) {
    if (!str) return '';
    return str
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  isDesktop() {
    return window.innerWidth >= 750;
  }

  initializeVariantSelection() {
    const productForm = document.querySelector('form[action*="/cart/add"]');
    const urlVariantId = this.getVariantFromURL();
    
    if (productForm) {
      const hasSelectedVariant = productForm.querySelector('input[name="id"]:checked, select[name="id"] option:checked');
      
      if (urlVariantId) {
        const urlVariant = this.productVariants.find(v => v.id === urlVariantId);
        if (urlVariant) {
          const variantInput = productForm.querySelector(`input[name="id"][value="${urlVariantId}"]`);
          if (variantInput) {
            variantInput.checked = true;
            variantInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
          
          setTimeout(() => {
            this.updateMainProductVisualSelectors();
          }, 100);
        }
      } else if (!hasSelectedVariant && this.productVariants.length > 0) {
        const firstAvailableVariant = this.productVariants.find(v => v.available) || this.productVariants[0];
        if (firstAvailableVariant) {
          const variantInput = productForm.querySelector(`input[name="id"][value="${firstAvailableVariant.id}"]`);
          if (variantInput) {
            variantInput.checked = true;
            variantInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }
    }
  }

  preloadImages() {
    const imagesToPreload = Math.min(3, this.productImages.length);
    
    for (let i = 0; i < imagesToPreload; i++) {
      const img = new Image();
      // Résolution adaptative : 1200px mobile, 2400px desktop
      const targetWidth = this.isDesktop() ? 2400 : 1200;
      const highResSrc = this.productImages[i].src.replace(/width=\d+/, `width=${targetWidth}`);
      img.src = highResSrc;
    }
  }
}

// Mobile Gallery Navigation
class MobileGalleryNavigation {
  constructor() {
    this.mainImage = document.getElementById('MainProductImage');
    this.dots = document.querySelectorAll('.product__media-dot');
    this.currentIndex = 0;
    this.mediaData = this.getMediaData();
    
    if (window.innerWidth <= 989) {
      this.init();
    }
  }

  getMediaData() {
    // This will be populated by Liquid template
    return window.mediaData || [];
  }

  init() {
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.switchToImage(index);
      });
    });

    if (this.mainImage) {
      let startX = 0;
      let startY = 0;

      (this.mainImage.closest('.product__media-main') || this.mainImage).addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true });
      
      (this.mainImage.closest('.product__media-main') || this.mainImage).addEventListener('touchend', (e) => {
        if (!e.changedTouches) return;
        
        const endX = e.changedTouches[0].clientX;
        const endY = e.changedTouches[0].clientY;
        const diffX = startX - endX;
        const diffY = Math.abs(startY - endY);
        
        if (Math.abs(diffX) > 50 && diffY < 100) {
          e.preventDefault();
          
          if (diffX > 0) {
            const nextIndex = (this.currentIndex + 1) % this.mediaData.length;
            this.switchToImage(nextIndex);
          } else {
            const prevIndex = this.currentIndex - 1 < 0 ? this.mediaData.length - 1 : this.currentIndex - 1;
            this.switchToImage(prevIndex);
          }
        }
      }, { passive: false });
    }
  }

  switchToImage(index) {
    if (this.mediaData[index] && this.mainImage) {
      // Clear srcset to force browser to use src (fixes swipe not changing image)
      this.mainImage.srcset = '';
      this.mainImage.src = this.mediaData[index].src;
      this.mainImage.alt = this.mediaData[index].alt;

      // Bascule image / video selon le type du media
      const mediaItem = this.mediaData[index];
      const videoEl = document.getElementById('MainProductVideo');
      if (videoEl) {
        if (mediaItem.type === 'video' && mediaItem.videoSrc) {
          if (videoEl.getAttribute('src') !== mediaItem.videoSrc) {
            videoEl.setAttribute('src', mediaItem.videoSrc);
          }
          videoEl.poster = mediaItem.poster || mediaItem.src;
          videoEl.hidden = false;
          this.mainImage.hidden = true;
          videoEl.play().catch(() => {});
        } else {
          videoEl.pause();
          videoEl.hidden = true;
          this.mainImage.hidden = false;
        }
      }
      
      // Update dots
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('active', i === index);
      });
      
      this.currentIndex = index;
      
      // Preload adjacent images for smoother swipe
      const nextIndex = (index + 1) % this.mediaData.length;
      const prevIndex = index - 1 < 0 ? this.mediaData.length - 1 : index - 1;
      
      if (this.mediaData[nextIndex] && this.mediaData[nextIndex].src) {
        new Image().src = this.mediaData[nextIndex].src;
      }
      if (this.mediaData[prevIndex] && this.mediaData[prevIndex].src) {
        new Image().src = this.mediaData[prevIndex].src;
      }
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Initialize mobile gallery navigation
  const mobileGallery = new MobileGalleryNavigation();

  // Premier media = video : on l'affiche tout de suite au lieu de sa vignette
  const firstMedia = (window.mediaData || [])[0];
  if (firstMedia && firstMedia.type === 'video' && firstMedia.videoSrc) {
    mobileGallery.switchToImage(0);
  }
  
  // Initialize product zoom modal
  new ProductZoomModal();
});
